import { EditNote, FactCheck } from '@tempo-io/sui-icons';
import { Separator } from '@tempo-io/sui-separator';
import { useTranslation } from 'react-i18next';
import { EmptyFieldValue } from '@/components/common/item-field-value/EmptyFieldValue';
import type { PublishedItemDetailsTabAdapter } from '@/components/modular-item-panel/adapters/types';
import { useTabAdapter } from '@/components/modular-item-panel/context/TabAdapterContext';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import { DateFormat, formatLocalDate } from '@/lib/utils/common/dateFormats';

export const PublishedItemDetails = () => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<PublishedItemDetailsTabAdapter>();

  const description = adapter.getFieldValue('description');
  const targetStartDateTime = adapter.getFieldValue('targetStartDateTime');
  const targetFinishDateTime = adapter.getFieldValue('targetFinishDateTime');

  const selectedItemTypeOption = adapter.getItemTypeOption();
  const selectedStatusOption = adapter.getStatusOption();
  const selectedTeamOption = adapter.getTeamOption();
  const parentItem = adapter.getParentItem();
  const selectedEstimationOption = adapter.getEstimationOption();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center gap-1 text-lg font-bold text-text-neutral-secondary">
        <EditNote /> {t(($) => $.adaptivePlanner.itemPanel.itemSection.itemDetails)}
      </div>

      <div className="grid w-full grid-cols-2 gap-4">
        {adapter.isVisible('itemTypeId') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.itemType)}
            </div>
            {selectedItemTypeOption ? (
              <div className="flex items-center gap-2 text-sm text-text-neutral-primary">
                <ItemTypeIcon
                  itemType={{
                    id: selectedItemTypeOption.value,
                    name: selectedItemTypeOption.label,
                    description: null,
                    itemLevelId: 0,
                    color: selectedItemTypeOption.color ?? null,
                    icon: selectedItemTypeOption.icon ?? null,
                    createdAt: '',
                    updatedAt: '',
                    createdBy: '',
                    updatedBy: '',
                  }}
                />
                <span>{selectedItemTypeOption.label}</span>
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('itemStatusId') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.itemStatus)}
            </div>
            {selectedStatusOption ? (
              <ItemStatusBadge
                color={selectedStatusOption.color ?? '#6B7280'}
                label={selectedStatusOption.label}
              />
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('parentId') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.parent)}
            </div>
            {parentItem ? (
              <div className="flex items-center gap-2 text-sm text-text-neutral-primary">
                {(parentItem.itemTypeIcon || parentItem.itemTypeColor) && (
                  <ItemTypeIcon
                    itemType={{
                      id: parentItem.id,
                      name: parentItem.name,
                      description: null,
                      itemLevelId: 0,
                      color: parentItem.itemTypeColor ?? null,
                      icon: parentItem.itemTypeIcon ?? null,
                      createdAt: '',
                      updatedAt: '',
                      createdBy: '',
                      updatedBy: '',
                    }}
                  />
                )}
                <span>{parentItem.name}</span>
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}
      </div>

      {adapter.isVisible('description') && (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">
            {t(($) => $.adaptivePlanner.itemPanel.itemSection.itemDescription)}
          </div>
          {description ? (
            <div className="text-sm text-text-neutral-primary">{description}</div>
          ) : (
            <EmptyFieldValue />
          )}
        </div>
      )}

      <Separator />

      <div className="flex flex-row items-center gap-1 text-lg font-bold text-text-neutral-secondary">
        <FactCheck /> {t(($) => $.adaptivePlanner.itemPanel.itemSection.workDetails)}
      </div>

      <div className="grid w-full grid-cols-2 gap-4">
        {adapter.isVisible('targetStartDateTime') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.targetStartDate)}
            </div>
            {targetStartDateTime ? (
              <div className="text-sm text-text-neutral-primary">
                {formatLocalDate(targetStartDateTime, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)}
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('targetFinishDateTime') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.targetFinishDate)}
            </div>
            {targetFinishDateTime ? (
              <div className="text-sm text-text-neutral-primary">
                {formatLocalDate(targetFinishDateTime, DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)}
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('teamId') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.teamAssignment)}
            </div>
            {selectedTeamOption ? (
              <div className="flex items-center gap-2 text-sm text-text-neutral-primary">
                <span>{selectedTeamOption.label}</span>
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('estimationId') && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.itemSection.effortEstimate)}
            </div>
            {selectedEstimationOption ? (
              <div className="text-sm text-text-neutral-primary">
                {selectedEstimationOption.label}
              </div>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
